import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Typography, Badge, Progress, Skeleton, Avatar, Space, Divider } from 'antd';

const {Text} = Typography;

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 24px;
  min-height: 100vh;
  background: ${(props) => getThemeValue(props, '#171923', '#f5f5f5')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderIconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const HeaderIconButton = styled(Button)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')} !important;
  font-size: 18px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  
  &:hover {
    color: ${(props) => getThemeValue(props, '#fff', '#1a202c')} !important;
    background: ${(props) => getThemeValue(props, 'rgba(255, 255, 255, 0.1)', 'rgba(0, 0, 0, 0.05)')} !important;
  }
`;

export const TitleWithIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MainTitle = styled.div`
  font-size: 28px !important;
  font-weight: 700 !important;
  line-height: normal;
  margin-bottom: 3px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')} !important;
  
  @media (max-width: 768px) {
    font-size: 24px !important;
  }
`;

export const Subtitle = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#18181B')};
  line-height: normal;
`;

export const GreetingCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  margin-bottom: 32px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

export const StatCard = styled(Card)<{ isSelected?: boolean; accentColor?: string }>`
  border-radius: 16px;
  border: 1px solid ${(props) => 
    props.isSelected 
      ? (props.accentColor || '#1890ff')
      : getThemeValue(props, '#4a5568', '#e2e8f0')
  };
  background: ${(props) => 
    props.isSelected 
      ? getThemeValue(props, '#1a3a5c', '#e6f7ff')
      : getThemeValue(props, '#1a202c', '#fff')
  };
  box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.15)', '0 2px 8px rgba(0, 0, 0, 0.1)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 100%;
  min-height: 200px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ accentColor }) => accentColor || '#1890ff'};
    z-index: 1;
    border-radius: 16px 16px 0 0;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => getThemeValue(props, '0 12px 32px rgba(0, 0, 0, 0.2)', '0 8px 24px rgba(0, 0, 0, 0.12)')};
    border-color: ${({ accentColor, isSelected }) => 
      isSelected ? (accentColor || '#1890ff') : 'transparent'
    };
  }
  
  .ant-card-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    
    @media (max-width: 768px) {
      padding: 20px;
    }
    
    @media (max-width: 480px) {
      padding: 16px;
    }
  }
`;

export const StatCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
`;

export const StatCardTitle = styled(Text)`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 11px;
    letter-spacing: 0.8px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    letter-spacing: 0.5px;
  }
`;

export const StatCardIcon = styled.div<{ bg?: string; color?: string }>`
  padding: 12px;
  border-radius: 12px;
  background: ${({ bg }) => bg || 'rgba(24, 144, 255, 0.1)'};
  color: ${({ color }) => color || '#1890ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    padding: 8px;
  }
  
  svg {
    width: 24px;
    height: 24px;
    
    @media (max-width: 768px) {
      width: 20px;
      height: 20px;
    }
    
    @media (max-width: 480px) {
      width: 18px;
      height: 18px;
    }
  }
`;

export const StatCardValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  line-height: 1.2;
  margin-bottom: 8px;
  word-break: break-word;
  letter-spacing: -0.5px;
  
  @media (max-width: 1024px) {
    font-size: 32px;
  }
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 6px;
  }
`;

export const StatCardSubtitle = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
  margin-bottom: 12px;
  line-height: 1.5;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 8px;
  }
`;

export const TrendBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: ${({ color }) => {
    // Convert hex to rgba with 15% opacity
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, 0.15)`;
  }};
  color: ${({ color }) => color};
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 16px;
  line-height: 1.4;
  
  svg {
    width: 12px;
    height: 12px;
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 5px 10px;
    margin-bottom: 14px;
    gap: 5px;
    
    svg {
      width: 11px;
      height: 11px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    padding: 4px 8px;
    margin-bottom: 12px;
    gap: 4px;
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

export const ProgressContainer = styled.div`
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#2d3748', '#f0f0f0')};
  
  @media (max-width: 768px) {
    padding-top: 10px;
  }
  
  @media (max-width: 480px) {
    padding-top: 8px;
  }
`;

export const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 11px;
  color: ${(props) => getThemeValue(props, '#a0aec0', '#718096')};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 10px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 9px;
    margin-bottom: 5px;
  }
`;

export const StyledProgress = styled(Progress)<{ strokeColor?: string }>`
  .ant-progress-outer {
    margin-right: 0;
    padding-right: 0;
  }
  
  .ant-progress-bg {
    background-color: ${({ strokeColor }) => (strokeColor || '#1890ff')} !important;
    border-radius: 4px;
    height: 8px !important;
  }
  
  .ant-progress-inner {
    background-color: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')};
    border-radius: 4px;
    overflow: hidden;
  }
  
  @media (max-width: 768px) {
    .ant-progress-bg {
      height: 6px !important;
    }
  }
  
  @media (max-width: 480px) {
    .ant-progress-bg {
      height: 5px !important;
    }
  }
`;

export const SuggestionCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => getThemeValue(props, '0 8px 24px rgba(0, 0, 0, 0.4)', '0 8px 24px rgba(0, 0, 0, 0.15)')};
  }
  
  .ant-card-body {
    padding: 24px;
  }
`;

export const SuggestionBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 8px;
    height: 8px;
  }
`;

export const SidebarCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  position: sticky;
  top: 24px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  @media (min-width: 992px) and (max-width: 1450px) {
    position: sticky;
    top: 24px;
  }
  
  @media (max-width: 991px) {
    position: relative;
    top: 0;
    margin-top: 24px;
  }
  
  .ant-card-body {
    padding: 24px;
    
    @media (min-width: 992px) and (max-width: 1200px) {
      padding: 20px;
    }
    
    @media (max-width: 768px) {
      padding: 20px;
    }
    
    @media (max-width: 576px) {
      padding: 16px;
    }
  }
`;

export const SidebarButton = styled(Button)`
  width: 100%;
  height: auto;
  padding: 16px;
  border-radius: 12px;
  /* border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')}; */
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  transition: all 0.2s ease-in-out;
  
  @media (max-width: 768px) {
    padding: 14px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 576px) {
    padding: 12px;
    margin-bottom: 8px;
  }
  
  &:hover {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f0f0f0')};
  }
  
  .ant-btn-icon {
    margin-right: 12px;
    
    @media (max-width: 576px) {
      margin-right: 8px;
    }
  }
`;

export const DetailCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  margin-bottom: 24px;
  min-height: 400px;
  
  .ant-card-body {
    padding: 24px;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
  width: 100%;
  
  /* When sidebar is visible (lg breakpoint), adjust columns for PC screens */
  @media (min-width: 992px) and (max-width: 1199px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }
  
  @media (min-width: 1200px) and (max-width: 1399px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  
  @media (min-width: 1400px) and (max-width: 1450px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
  
  @media (min-width: 1451px) and (max-width: 1599px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 18px;
  }
  
  @media (min-width: 1600px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
  
  /* When sidebar is not visible (mobile/tablet) */
  @media (max-width: 991px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  
  @media (min-width: 768px) and (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
  
  @media (min-width: 576px) and (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  @media (max-width: 575px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 24px;
  }
`;

export const SuggestionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ValueSkeleton = styled(Skeleton.Input)`
  width: 100px;
  height: 32px;
  margin-bottom: 8px;
`;

export const ProgressSkeleton = styled(Skeleton.Input)`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  margin-top: 8px;
`;

export const ProgressSkeletonSmall = styled(Skeleton.Input)`
  width: 30px;
  height: 14px;
`;

// Greeting Component Styles
export const GreetingSpace = styled(Space)`
  width: 100%;
`;

export const GreetingAvatar = styled(Avatar)`
  background-color: #1890ff !important;
`;

export const GreetingTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const GreetingSubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: #8c8c8c;
`;

export const GreetingIconWrapper = styled.span`
  color: #8c8c8c;
`;

export const GreetingText = styled.span`
  font-size: 12px;
  color: #8c8c8c;
`;

// Sidebar Component Styles
export const SidebarSpace = styled(Space)`
  width: 100%;
`;

export const SidebarHeader = styled.h2`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
  
  @media (max-width: 576px) {
    font-size: 16px;
    margin-bottom: 6px;
  }
`;

export const SidebarDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #8c8c8c;
  
  @media (max-width: 576px) {
    font-size: 12px;
  }
`;

export const SidebarIconWrapper = styled.span<{ color?: string }>`
  color: ${({ color }) => color || '#1890ff'};
  font-size: 18px;
`;

export const SidebarButtonContent = styled.div`
  flex: 1;
  text-align: left;
`;

export const SidebarButtonText = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
  
  @media (max-width: 576px) {
    font-size: 13px;
  }
`;

export const SidebarButtonDescription = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  
  @media (max-width: 576px) {
    font-size: 11px;
  }
`;

export const SidebarArrowIconWrapper = styled.span`
  color: #8c8c8c;
`;

export const SidebarDivider = styled(Divider)`
  margin: 0px;
  border-color: ${(props) => getThemeValue(props, '#4a5568', '#e2e8f0')};
`;

export const SidebarSectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const SidebarActivityIconWrapper = styled.span<{ color?: string }>`
  color: ${({ color }) => color || '#8c8c8c'};
  font-size: 8px;
`;

export const SidebarActivityText = styled.span`
  font-size: 12px;
  color: #8c8c8c;
`;

export const SidebarActivitySpace = styled(Space)`
  width: 100%;
`;

// Detail Card Header Styles
export const DetailCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-top: 12px;
`;

export const DetailCardTitleSection = styled.div``;

export const DetailCardTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const DetailCardSubtitle = styled.p`
  font-size: 14px;
  color: #8c8c8c;
`;

export const DetailCardCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DetailCardCloseIconWrapper = styled.span`
  font-size: 16px;
  color: #8c8c8c;
  &:hover {
    color: #ff4d4f;
  }
`;

// Devices/Sessions Overview Styles
export const OverviewContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const OverviewValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const OverviewLabel = styled.div`
  font-size: 14px;
  color: #8c8c8c;
`;

export const OverviewButton = styled(Button)`
  border-radius: 24px;
`;

export const ChartContainer = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EmptyIconWrapper = styled.span`
  font-size: 64px;
  color: #8c8c8c;
`;

export const EmptyText = styled.span`
  color: #8c8c8c;
`;

// Suggestion Card Styles
export const SuggestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const SuggestionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a202c')};
`;

export const SuggestionDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.6;
`;

export const SuggestionIconWrapper = styled.span<{ color?: string }>`
  color: ${({ color }) => color || '#1890ff'};
  font-size: 20px;
`;

export const SuggestionActionButton = styled(Button)<{ bgColor?: string; borderColor?: string }>`
  width: 100%;
  background-color: ${({ bgColor }) => bgColor || '#1890ff'} !important;
  border-color: ${({ borderColor }) => borderColor || '#1890ff'} !important;
`;

// Header Button Styles
export const HeaderButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.7;
  }
`;

export const HeaderIconWrapper = styled.span`
  font-size: 20px;
  color: #8c8c8c;
`;

// Recommendations Section Styles
export const RecommendationsSection = styled.div``;

export const RecommendationsHeader = styled.div`
  width: 100%;
`;